import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function AdminProperties() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === 'admin';

  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ unit_code: '', name: '', address: '', lat: '', lng: '', owner_id: '', active: true });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Map state
  const [showMap, setShowMap] = useState(false);
  const [testingAddress, setTestingAddress] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
    async function loadData() {
      try {
        if (isAdmin) {
          const [propsData, ownersData] = await Promise.all([
            api.get('/admin/properties'),
            api.get('/admin/owners')
          ]);
          setProperties(propsData);
          setOwners(ownersData);
        } else {
          const propsData = await api.get('/owner/properties');
          setProperties(propsData);
        }
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [user, isAdmin]);

  async function refreshData() {
    try {
      if (isAdmin) {
        const [propsData, ownersData] = await Promise.all([
          api.get('/admin/properties'),
          api.get('/admin/owners')
        ]);
        setProperties(propsData);
        setOwners(ownersData);
      } else {
        const propsData = await api.get('/owner/properties');
        setProperties(propsData);
      }
    } catch (err) {
      console.error('Failed to load:', err);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const data = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
        owner_id: form.owner_id || null
      };

      if (editingId) {
        await api.put(`/admin/properties/${editingId}`, data);
      } else {
        await api.post('/admin/properties', data);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ unit_code: '', name: '', address: '', lat: '', lng: '', owner_id: '', active: true });
      refreshData();
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    }
  }

  function handleEdit(property) {
    setForm({
      unit_code: property.unit_code,
      name: property.name,
      address: property.address || '',
      lat: property.lat || '',
      lng: property.lng || '',
      owner_id: property.owner_id || '',
      active: property.active
    });
    setEditingId(property.id);
    setShowForm(true);
  }

  async function handleDelete(id) {
    if (!window.confirm(t('admin.deleteProperty'))) return;
    try {
      await api.delete(`/admin/properties/${id}`);
      refreshData();
    } catch (err) {
      alert(t('common.errorOccurred', { message: err.message }));
    }
  }

  // Test address using Nominatim (OpenStreetMap geocoding)
  async function handleTestAddress() {
    if (!form.address) {
      alert(t('common.required'));
      return;
    }
    setTestingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.address)}&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        setForm(f => ({ ...f, lat: lat, lng: lon }));
        alert(`${t('admin.addressFound')}\n\n${display_name}\nLat: ${lat}\nLng: ${lon}`);
      } else {
        alert(t('admin.addressNotFound'));
      }
    } catch (err) {
      alert(`${t('admin.geocodingFailed')}: ${err.message}`);
    } finally {
      setTestingAddress(false);
    }
  }

  // Filter and sort properties
  const filteredProperties = properties
    .filter(p => {
      const matchesSearch = !searchTerm ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.unit_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.address && p.address.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesOwner = !filterOwner || p.owner_id === filterOwner;
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && p.active) ||
        (filterStatus === 'inactive' && !p.active);
      return matchesSearch && matchesOwner && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'unit_code') return a.unit_code.localeCompare(b.unit_code);
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  // Initialize map when modal opens
  useEffect(() => {
    if (showMap && !mapRef.current) {
      // Dynamically load Leaflet
      const loadLeaflet = async () => {
        if (!window.L) {
          // Add Leaflet CSS
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
          // Add Leaflet JS
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          script.onload = () => initializeMap();
          document.head.appendChild(script);
        } else {
          initializeMap();
        }
      };

      const initializeMap = () => {
        // Fix Leaflet default marker icon
        delete window.L.Icon.Default.prototype._getIconUrl;
        window.L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        });
        const lat = form.lat ? parseFloat(form.lat) : 12.93;
        const lng = form.lng ? parseFloat(form.lng) : 100.88;

        mapRef.current = window.L.map('property-map').setView([lat, lng], 15);
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(mapRef.current);

        markerRef.current = window.L.marker([lat, lng], { draggable: true }).addTo(mapRef.current);

        markerRef.current.on('dragend', function(e) {
          const pos = e.target.getLatLng();
          setForm(f => ({ ...f, lat: pos.lat.toFixed(6), lng: pos.lng.toFixed(6) }));
        });

        mapRef.current.on('click', function(e) {
          markerRef.current.setLatLng(e.latlng);
          setForm(f => ({ ...f, lat: e.latlng.lat.toFixed(6), lng: e.latlng.lng.toFixed(6) }));
        });
      };

      loadLeaflet();
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [showMap, form.lat, form.lng]);


  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 style={{ fontSize: '20px', fontWeight: '600' }}>{t('admin.properties')}</h1>
        {isAdmin && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ unit_code: '', name: '', address: '', lat: '', lng: '', owner_id: '', active: true }); }} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }}>
            + {t('common.new')}
          </button>
        )}
      </div>
        {/* Filters */}
        <div className="card mb-4">
          <div className="flex gap-2 flex-wrap items-end">
            <div style={{ flex: '2', minWidth: '200px' }}>
              <label className="label text-xs">{t('common.search')}</label>
              <input
                type="text"
                className="input"
                placeholder={t('common.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {isAdmin && (
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label className="label text-xs">{t('admin.filterByOwner')}</label>
                <select className="input" value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
                  <option value="">{t('admin.allOwners')}</option>
                  {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.status')}</label>
              <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">{t('common.all')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.sortBy')}</label>
              <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">{t('admin.nameAZ')}</option>
                <option value="unit_code">{t('admin.unitCode')}</option>
                <option value="newest">{t('admin.newestFirst')}</option>
                <option value="oldest">{t('admin.oldestFirst')}</option>
              </select>
            </div>
          </div>
          <div className="text-secondary text-xs mt-2">
            {t('common.showing', { count: filteredProperties.length, total: properties.length })}
          </div>
        </div>

        {showForm && (
          <div className="card mb-4">
            <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>{editingId ? t('admin.editProperty') : t('admin.newProperty')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">{t('admin.unitCode')} *</label>
                <input type="text" className="input" value={form.unit_code} onChange={(e) => setForm(f => ({ ...f, unit_code: e.target.value }))} required placeholder="e.g., SUNSET-101" />
                <p className="text-secondary text-xs mt-1">{t('admin.unitCodeHelp')}</p>
              </div>
              <div className="form-group">
                <label className="label">{t('admin.propertyName')} *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g., Sunset Beach Villa" />
              </div>
              <div className="form-group">
                <label className="label">{t('common.address')}</label>
                <div className="flex gap-2">
                  <input type="text" className="input" style={{ flex: 1 }} value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} />
                  <button type="button" className="btn btn-secondary" onClick={handleTestAddress} disabled={testingAddress} style={{ whiteSpace: 'nowrap' }}>
                    {testingAddress ? t('admin.testing') : t('admin.testAddress')}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="label">{t('admin.latitude')}</label>
                  <input type="number" step="any" className="input" value={form.lat} onChange={(e) => setForm(f => ({ ...f, lat: e.target.value }))} placeholder="12.9276" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="label">{t('admin.longitude')}</label>
                  <input type="number" step="any" className="input" value={form.lng} onChange={(e) => setForm(f => ({ ...f, lng: e.target.value }))} placeholder="100.8770" />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowMap(true)} style={{ whiteSpace: 'nowrap' }}>
                    {t('admin.pickOnMap')}
                  </button>
                </div>
              </div>
              {isAdmin && (
                <div className="form-group">
                  <label className="label">{t('admin.owner')}</label>
                  <select className="input" value={form.owner_id} onChange={(e) => setForm(f => ({ ...f, owner_id: e.target.value }))}>
                    <option value="">{t('admin.noOwner')}</option>
                    {owners.map(o => <option key={o.id} value={o.id}>{o.name} ({o.email})</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label><input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} /> {t('common.active')}</label>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary">{t('common.save')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        )}

        {/* Map Modal */}
        {showMap && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden' }}>
              <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '16px' }}>{t('admin.pickLocation')}</h3>
                <button onClick={() => setShowMap(false)} className="btn btn-secondary" style={{ padding: '4px 12px' }}>{t('common.done')}</button>
              </div>
              <div id="property-map" style={{ height: '400px' }}></div>
              <div style={{ padding: '12px', background: 'var(--background)', fontSize: '13px' }}>
                <strong>Tip:</strong> {t('admin.mapTip')} {t('admin.current')}: {form.lat || '—'}, {form.lng || '—'}
              </div>
            </div>
          </div>
        )}

        {filteredProperties.length === 0 ? (
          <div className="card text-center text-secondary">
            {t('admin.noPropertiesFound')}
          </div>
        ) : (
          filteredProperties.map(p => (
            <div key={p.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Link to={`/properties/${p.id}`} style={{ flex: 1, textDecoration: 'none', color: 'inherit' }}>
                <div className="flex items-center gap-2">
                  <h3 style={{ fontSize: '14px' }}>{p.name}</h3>
                  {!p.active && <span className="badge" style={{ background: 'var(--error)', color: 'white', fontSize: '10px', padding: '2px 6px' }}>{t('common.inactive')}</span>}
                </div>
                <p className="text-secondary text-xs">{p.unit_code} {p.owner_name && `• ${p.owner_name}`}</p>
                {p.address && <p className="text-secondary text-xs">{p.address}</p>}
              </Link>
              <div className="flex gap-2">
                {isAdmin && (
                  <>
                    <button onClick={() => handleEdit(p)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>{t('common.edit')}</button>
                    <button onClick={() => handleDelete(p.id)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px', color: 'var(--error)' }}>{t('common.delete')}</button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
    </div>
  );
}

export default AdminProperties;
